import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface ExternalSchool {
  id: string
  name: string
  status: 'active' | 'archived'
  address: string | null
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  hourlyRate: number | null
  rateCurrency: string
}

export interface SchoolFields {
  name: string
  address?: string | null
  contactName?: string | null
  contactEmail?: string | null
  contactPhone?: string | null
  hourlyRate?: number | null
}

interface UseInstructorExternalSchoolsReturn {
  schools: ExternalSchool[]
  loading: boolean
  error: string | null
  creating: boolean
  updating: string | null
  createSchool: (fields: SchoolFields) => Promise<{ ok: boolean; error?: string }>
  updateSchool: (id: string, fields: SchoolFields) => Promise<{ ok: boolean; error?: string }>
  archiveSchool: (id: string) => Promise<{ ok: boolean; error?: string }>
  reactivateSchool: (id: string) => Promise<{ ok: boolean; error?: string }>
  refresh: () => void
}

function fromRow(row: any): ExternalSchool {
  return {
    id: row.id,
    name: row.name,
    status: row.status,
    address: row.address ?? null,
    contactName: row.contact_name ?? null,
    contactEmail: row.contact_email ?? null,
    contactPhone: row.contact_phone ?? null,
    hourlyRate: row.hourly_rate === null || row.hourly_rate === undefined ? null : Number(row.hourly_rate),
    rateCurrency: row.rate_currency,
  }
}

/**
 * An individual instructor's own record of schools they work with that are NOT
 * DriveTrack tenants (migration 055, public.instructor_external_schools) — including the
 * contractor rate/currency/contact fields added in migration 058. Attribution labels
 * only — never create school_memberships, never grant tenant access, never visible to
 * anyone but the owning instructor. RLS alone authorizes every operation here (own
 * select/insert/update policies); no RPC is needed for this table's own CRUD.
 */
export function useInstructorExternalSchools(): UseInstructorExternalSchoolsReturn {
  const { user } = useAuth()
  const [schools, setSchools] = useState<ExternalSchool[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [creating, setCreating] = useState(false)
  const [updating, setUpdating] = useState<string | null>(null)

  const fetchSchools = useCallback(async () => {
    if (!user) return
    setLoading(true)
    setError(null)
    try {
      const { data, error: fetchErr } = await supabase
        .from('instructor_external_schools')
        .select('id, name, status, address, contact_name, contact_email, contact_phone, hourly_rate, rate_currency')
        .order('name', { ascending: true })

      if (fetchErr) { setError('Could not load your schools.'); return }
      setSchools((data ?? []).map(fromRow))
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchSchools() }, [fetchSchools])

  const createSchool = useCallback(async (fields: SchoolFields) => {
    if (!user) return { ok: false, error: 'Not signed in.' }
    setCreating(true)
    try {
      // instructor_id is set explicitly to the caller's own id; the INSERT policy's
      // WITH CHECK independently re-verifies this matches auth.uid() and that the
      // caller is an instructor — this client-supplied value is never trusted alone.
      // hourly_rate's positivity (when provided) is independently re-enforced by
      // chk_instructor_external_schools_hourly_rate_positive (migration 058) regardless
      // of whatever this form validated client-side.
      const { error: insertErr } = await supabase
        .from('instructor_external_schools')
        .insert({
          instructor_id: user.id,
          name: fields.name,
          address: fields.address ?? null,
          contact_name: fields.contactName ?? null,
          contact_email: fields.contactEmail ?? null,
          contact_phone: fields.contactPhone ?? null,
          hourly_rate: fields.hourlyRate ?? null,
        })

      if (insertErr) return { ok: false, error: 'Could not create the school. Please check the hourly rate and try again.' }
      await fetchSchools()
      return { ok: true }
    } finally {
      setCreating(false)
    }
  }, [user, fetchSchools])

  const updateSchool = useCallback(async (id: string, fields: SchoolFields) => {
    setUpdating(id)
    try {
      const { error: updateErr } = await supabase
        .from('instructor_external_schools')
        .update({
          name: fields.name,
          address: fields.address ?? null,
          contact_name: fields.contactName ?? null,
          contact_email: fields.contactEmail ?? null,
          contact_phone: fields.contactPhone ?? null,
          hourly_rate: fields.hourlyRate ?? null,
        })
        .eq('id', id)

      if (updateErr) return { ok: false, error: 'Could not update this school. Please check the hourly rate and try again.' }
      await fetchSchools()
      return { ok: true }
    } finally {
      setUpdating(null)
    }
  }, [fetchSchools])

  const archiveSchool = useCallback(async (id: string) => {
    // Archive only — there is no DELETE policy on this table (migration 055), so a
    // historical attribution on instructor_student_relationships (or now, a pending
    // invitation's external_school_id — migration 058) can never dangle.
    const { error: updateErr } = await supabase
      .from('instructor_external_schools')
      .update({ status: 'archived' })
      .eq('id', id)

    if (updateErr) return { ok: false, error: 'Could not archive this school. Please try again.' }
    await fetchSchools()
    return { ok: true }
  }, [fetchSchools])

  const reactivateSchool = useCallback(async (id: string) => {
    const { error: updateErr } = await supabase
      .from('instructor_external_schools')
      .update({ status: 'active' })
      .eq('id', id)

    if (updateErr) return { ok: false, error: 'Could not reactivate this school. Please try again.' }
    await fetchSchools()
    return { ok: true }
  }, [fetchSchools])

  return { schools, loading, error, creating, updating, createSchool, updateSchool, archiveSchool, reactivateSchool, refresh: fetchSchools }
}
